import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  authApi,
  clearToken,
  getToken,
  isUnauthorized,
  setToken,
  UserData,
} from "../utils/authApi";
import { refreshPackageRegistry } from "../registry/packageRegistryBootstrap";
import { UserContext } from "./userContext";
import { Loading } from "../components/login/Loading";
import { isShareLinkPath } from "../utils/shareLinks";
import { isStandaloneDashboard } from "../standalone/dashboardPayload";
import { mayEditServedDashboard } from "../standalone/servedDashboardAccess";

/**
 * Who is looking at a page that was served complete.
 *
 * Not a real account and never sent anywhere: it exists so `RequireAuth` lets
 * the page render, and so everything downstream treats the viewer as the guest
 * they are, which is what makes the dashboard read-only.
 */
const STANDALONE_VIEWER: UserData = {
  id: 0,
  username: "guest_shared",
  name: "Viewer",
  email: null,
  profile_image: null,
  type: null,
  is_guest: true,
};

const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [enableUserAuth, setEnableUserAuth] = useState<boolean>(true);
  const [skipProjectPage, setSkipProjectPage] = useState<boolean>(false);
  const [allowGuest, setAllowGuest] = useState<boolean>(false);
  const [sharedGuestUsername, setSharedGuestUsername] =
    useState<string>("guest_shared");

  const applyUser = useCallback((nextUser: UserData) => {
    setUser(nextUser);
    // The per-user node-package store only responds once we carry a Bearer token.
    // Refresh immediately so the palette matches the Nodes hub - do not rely
    // solely on ``window.curio`` indirection which can silently no-op.
    void refreshPackageRegistry();
    return nextUser;
  }, []);

  const handleAuth = useCallback(
    (res: { user: UserData; token: string }) => {
      setToken(res.token);
      return applyUser(res.user);
    },
    [applyUser]
  );

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setLoading(true);
      try {
        // A standalone dashboard is a document, not a session. It still needs
        // a user, because `RequireAuth` shows a sign-in form to anyone without
        // one, and a page served complete must never ask a viewer to log in to
        // read it. So it gets the viewer it actually is: a guest with no
        // account, which is also what puts the page in its read-only
        // presentation.
        //
        // Unless this browser's session may edit the dashboard's layout, which
        // a session asks once (`mayEditServedDashboard`): then the page learns
        // who is signed in the way any page does, below, and ProjectLoader
        // loads it as its owner's.
        if (isStandaloneDashboard() && !(await mayEditServedDashboard())) {
          if (!cancelled) {
            setEnableUserAuth(false);
            setSkipProjectPage(false);
            setAllowGuest(true);
            setUser(STANDALONE_VIEWER);
          }
          return;
        }

        const cfg = await authApi.getPublicConfig().catch(() => {
          console.error(
            "[Curio] Could not reach backend at /api/config/public. " +
            "Is the backend running? Check terminal output."
          );
          return null;
        });
        const projectPageSkipped = Boolean(
          cfg?.skip_project_page ?? cfg?.curio_no_project ?? false
        );
        // ``enable_user_auth`` was removed from the public config: auth is
        // considered enabled unless the backend has explicitly opted out via
        // ``CURIO_NO_AUTH`` or the ``CURIO_NO_PROJECT`` shortcut.
        const authSkipped =
          Boolean(cfg?.curio_no_auth ?? false) || projectPageSkipped;
        const authEnabled = !authSkipped;
        const sharedGuestUsername = cfg?.shared_guest_username ?? "guest_shared";

        if (cancelled) return;

        setEnableUserAuth(authEnabled);
        setSkipProjectPage(projectPageSkipped);
        setAllowGuest(Boolean(authEnabled && cfg?.allow_guest_login));
        setSharedGuestUsername(sharedGuestUsername);
        const token = getToken();

        if (!authEnabled) {
          if (token) {
            let current: UserData | null = null;
            let refused = false;
            try {
              current = await authApi.getMe();
            } catch (e) {
              refused = isUnauthorized(e);
            }
            // A check that ends after the provider unmounted leaves the token
            // alone: the provider that replaced this one does its own.
            if (cancelled) return;
            if (current?.is_guest && current.username === sharedGuestUsername) {
              applyUser(current);
              return;
            }
            // Drop a token the server refused or one for another account. A
            // check that failed any other way says nothing about the token,
            // and the guest session below replaces it once it is issued.
            if (refused || current) clearToken();
            setUser(null);
          }

          const res = await authApi.signinAutoGuest();
          if (!cancelled) handleAuth(res);
          return;
        }

        if (!token) {
          // Share-link bootstrap: when an unauthenticated visitor lands on a
          // link to one dataflow - its canvas or its dashboard - and guest login
          // is allowed, sign them in as the shared guest so they can look at it
          // without facing a login form.
          const onShareUrl = isShareLinkPath(window.location.pathname);
          if (onShareUrl && cfg?.allow_guest_login) {
            try {
              const res = await authApi.signinGuest();
              if (!cancelled) handleAuth(res);
            } catch {
              if (!cancelled) setUser(null);
            }
            return;
          }
          if (!cancelled) setUser(null);
          return;
        }

        try {
          const current = await authApi.getMe();
          if (!cancelled) {
            applyUser(current);
          }
        } catch (e) {
          // Only a 401 ends the session. A check that was aborted (the page
          // navigating away), could not connect or met a server error says
          // nothing about it, so the token stays for the next load.
          if (isUnauthorized(e)) clearToken();
          if (!cancelled) setUser(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  // `loading` belongs to the mount-time bootstrap alone. It gates `children`
  // below, so setting it here would unmount whatever form is awaiting this
  // call: on a rejection a *fresh* form remounts with empty fields and no
  // error, because the catch that set the error ran on the old instance. The
  // forms track their own submitting state and disable their buttons.
  const signup = useCallback(
    async (data: {
      name: string;
      username: string;
      password: string;
      email?: string;
    }) => {
      const res = await authApi.signup(data);
      return handleAuth(res);
    },
    [handleAuth]
  );

  const signin = useCallback(
    async (identifier: string, password: string) => {
      const res = await authApi.signin({ identifier, password });
      return handleAuth(res);
    },
    [handleAuth]
  );

  const signinGuest = useCallback(async () => {
    try {
      const res = await authApi.signinGuest();
      return handleAuth(res);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [handleAuth]);

  const signout = useCallback(async () => {
    if (!enableUserAuth) {
      return;
    }
    try {
      await authApi.signout();
    } catch {
      return;
    }
    clearToken();
    setUser(null);
  }, [enableUserAuth]);

  const updateProfile = useCallback(
    async (data: { name?: string; email?: string; type?: string }) => {
      const updated = await authApi.patchMe(data);
      setUser(updated);
    },
    []
  );

  const updateTokens = useCallback(
    async (fields: { source_keys: Record<string, string> }) => {
      const updated = await authApi.patchMe(fields);
      setUser(updated);
    },
    []
  );

  const saveUserType = useCallback(
    async (newType: "programmer" | "expert") => {
      await updateProfile({ type: newType });
    },
    [updateProfile]
  );

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        enableUserAuth,
        skipProjectPage,
        allowGuest,
        sharedGuestUsername,
        isSharedGuest: Boolean(
          user?.is_guest && user.username === sharedGuestUsername
        ),
        signup,
        signin,
        signinGuest,
        signout,
        updateProfile,
        updateTokens,
        saveUserType,
        logout: signout,
      }}
    >
      {loading ? <Loading /> : children}
    </UserContext.Provider>
  );
};

export { UserContext, useUserContext } from "./userContext";

export default UserProvider;
